import * as THREE from "three";
import { STLExporter } from "three/examples/jsm/exporters/STLExporter.js";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import { FootMeshData } from "context/FootMeshContext";

// Foot scans: per foot, the geometry aligned onto the insole plane (binary STL, max 10 MB).
// Both feet are saved together.
export interface FootScanInfo {
  side: 'Right' | 'Left';
  fileSize: number;
  createdAt: string;
  updatedAt: string | null;
}

// A foot (imported or mirrored) and the matrix that places it on its insole (computeFootAlignMatrix).
export interface AlignedFoot {
  foot: FootMeshData;
  alignMatrix: THREE.Matrix4;
}

export const FOOT_SCAN_MAX_SIZE = 10 * 1024 * 1024;

const appendAlignedFoot = (formData: FormData, key: 'Right' | 'Left', { foot, alignMatrix }: AlignedFoot) => {
  const geometry = foot.geometry.clone().applyMatrix4(alignMatrix);
  const stl = new STLExporter().parse(new THREE.Mesh(geometry), { binary: true }) as DataView;
  geometry.dispose();
  if (stl.byteLength > FOOT_SCAN_MAX_SIZE) throw new Error(`${key} foot mesh is larger than 10 MB`);

  formData.append(`${key}.File`, new Blob([stl.buffer as ArrayBuffer], { type: "application/vnd.ms-pki.stl" }), `${key.toLowerCase()}.stl`);
};

export async function saveFootScans(api: any, designId: number, right: AlignedFoot, left: AlignedFoot): Promise<FootScanInfo[] | null> {
  const formData = new FormData();
  appendAlignedFoot(formData, 'Right', right);
  appendAlignedFoot(formData, 'Left', left);
  const res = await api.put(`/api/designs/${designId}/footscans`, formData);
  return res.ok ? res.body : null;
}

// Metadata of both feet (no file data).
export async function getFootScans(api: any, designId: number): Promise<FootScanInfo[]> {
  const res = await api.get(`/api/designs/${designId}/footscans`);
  return res.ok ? res.body : [];
}

// Binary STL already in insole coordinates: load it without any extra transform.
export async function getFootScanFile(api: any, designId: number, footSide: "right" | "left"): Promise<ArrayBuffer | null> {
  const res = await api.get(`/api/designs/${designId}/footscans/${footSide}/file`);
  return res.ok ? res.body : null;
}

// Both saved feet; a foot that was mirrored when saved loads like an imported one.
// The geometry is already in insole coordinates, so it is marked as placed (no re-alignment) and has no points.
export async function loadFootScans(api: any, designId: number): Promise<Record<"right" | "left", FootMeshData | null>> {
  const feet: Record<"right" | "left", FootMeshData | null> = { right: null, left: null };
  const scans = await getFootScans(api, designId);
  await Promise.all(scans.map(async scan => {
    const side = scan.side === 'Right' ? 'right' : 'left';
    const buffer = await getFootScanFile(api, designId, side);
    if (!buffer) return;
    const geometry = new STLLoader().parse(buffer);
    geometry.computeVertexNormals();
    feet[side] = { fileName: `Saved ${side} foot`, geometry, points: [], placed: true };
  }));
  return feet;
}

export async function deleteFootScans(api: any, designId: number): Promise<boolean> {
  const res = await api.delete(`/api/designs/${designId}/footscans`);
  return res.ok;
}
